import { ReactElement, useMemo } from 'react';

import { PractitionerView } from '@dreryk/egabinet-personnel-frontend-api';

import { BindingCreator, createTranslatedText, useTranslate } from '@dreryk/react-common';
import { FilterPanel, Multiselect } from '@dreryk/react-components';

import {
   WaitlistFilterPanelFormBean,
   WaitlistFilterPanelSearchParam,
   WaitlistFilterPanelStorageKey,
} from '../../WaitlistFilterPanel.types';
import { translationKeys } from '../../../translations';
import { formatPractitioner } from '../../../shared/formatPractitioner';

import {
   createPractitionersCodec,
   createPractitionersTriggerLabelExtractor,
   practitionersDeselectorsPropsMapper,
} from './PractitionersController.utils';
import { StyledPractitionersMultiselect } from './PractitionersMultiselect.styled';

type PractitionersControllerProps = {
   registerController: BindingCreator<WaitlistFilterPanelFormBean, 'practitioners'>;
   practitioners: PractitionerView[];
};

export const PractitionersController = (props: PractitionersControllerProps): ReactElement => {
   const translate = useTranslate();
   const codec = useMemo(() => createPractitionersCodec(props.practitioners), [props.practitioners]);
   return (
      <FilterPanel.Controller<WaitlistFilterPanelFormBean, 'practitioners'>
         name={'practitioners'}
         searchParam={WaitlistFilterPanelSearchParam.PRACTITIONER}
         storageKey={WaitlistFilterPanelStorageKey.PRACTITIONER}
         codec={codec}
         chipsLabel={translationKeys.specialist}
         getDeselectorsProps={practitionersDeselectorsPropsMapper}
      >
         {({ applyFilter }) => (
            <StyledPractitionersMultiselect<PractitionerView>
               testId={'practitioners'}
               triggerLabelExtractor={createPractitionersTriggerLabelExtractor(props.practitioners.length, translate)}
               controller={props.registerController('practitioners')}
               onHide={applyFilter}
               label={translationKeys.specialist}
               placeholder={translationKeys.chooseSpecialists}
               itemKeyGenerator={'id'}
               compact
            >
               <>
                  <Multiselect.SelectAll testId={'selectAll'} items={props.practitioners} />
                  <Multiselect.List<PractitionerView> items={props.practitioners} keyExtractor={'id'}>
                     {(item) => (
                        <Multiselect.Item
                           item={item}
                           label={createTranslatedText(formatPractitioner(item))}
                           testId={item.id}
                        />
                     )}
                  </Multiselect.List>
               </>
            </StyledPractitionersMultiselect>
         )}
      </FilterPanel.Controller>
   );
};
